<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white flex flex-col items-center justify-center relative overflow-hidden">
    <!-- Animated Background Elements -->
    <div class="absolute inset-0 opacity-10">
      <!-- Shopping Cart Icons -->
      <div class="absolute top-20 left-20 text-6xl animate-bounce" style="animation-delay: 0s; animation-duration: 3s;">🛒</div>
      <div class="absolute bottom-20 right-20 text-5xl animate-bounce" style="animation-delay: 1s; animation-duration: 4s;">🛍️</div>
      <div class="absolute top-1/3 right-1/4 text-4xl animate-bounce" style="animation-delay: 2s; animation-duration: 3.5s;">📦</div>
      <div class="absolute bottom-1/3 left-1/4 text-5xl animate-bounce" style="animation-delay: 1.5s; animation-duration: 4.5s;">💳</div>
    </div>
    
    <!-- Grid Pattern -->
    <div class="absolute inset-0 opacity-5" style="background-image: radial-gradient(circle, white 1px, transparent 1px); background-size: 50px 50px;"></div>
    
    <!-- Main Content -->
    <div class="relative z-10 text-center max-w-3xl mx-auto px-6">
      <!-- Large 404 with Shopping Cart Icon -->
      <div class="relative mb-8">
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-9xl opacity-20">
          🛒
        </div>
        <h1 class="text-[10rem] md:text-[14rem] font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 leading-none tracking-tight">
          404
        </h1>
      </div>
      
      <!-- Error Message -->
      <div class="mb-12">
        <h2 class="text-3xl md:text-5xl font-bold mb-4 text-white">
          Oops! Product Not Found
        </h2>
        <p class="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">
          Looks like this item is out of stock... from existence! 
          Don't worry, we have plenty of amazing products waiting for you.
        </p>
      </div>
      
      <!-- Action Buttons -->
      <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
        <a 
          href="/" 
          class="group px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 flex items-center gap-3"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
          </svg>
          Back to Home
        </a>
        
        <a 
          href="/products" 
          class="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-bold rounded-xl hover:bg-white/20 hover:border-white/40 transition-all duration-300 transform hover:scale-105 flex items-center gap-3"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
          </svg>
          Browse Products
        </a>
      </div>
      
      <!-- Quick Links -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        <a href="/trending" class="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-purple-500/50 transition-all duration-300 transform hover:scale-105">
          <div class="text-4xl mb-3 group-hover:scale-110 transition-transform">🏷️</div>
          <h3 class="text-lg font-bold text-white mb-2">Trending</h3>
          <p class="text-sm text-gray-400">Explore trending products</p>
        </a>
        
        <a href="/cart" class="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-pink-500/50 transition-all duration-300 transform hover:scale-105">
          <div class="text-4xl mb-3 group-hover:scale-110 transition-transform">🛒</div>
          <h3 class="text-lg font-bold text-white mb-2">Your Cart</h3>
          <p class="text-sm text-gray-400">View your shopping cart</p>
        </a>
        
        <a href="/orders" class="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-purple-500/50 transition-all duration-300 transform hover:scale-105">
          <div class="text-4xl mb-3 group-hover:scale-110 transition-transform">📦</div>
          <h3 class="text-lg font-bold text-white mb-2">Orders</h3>
          <p class="text-sm text-gray-400">Track your orders</p>
        </a>
      </div>
      
      <!-- Help Section -->
      <div class="mt-16 pt-8 border-t border-white/20">
        <p class="text-sm text-gray-400 mb-4">
          Learn more about our policies and terms
        </p>
        <div class="flex flex-wrap justify-center gap-6 text-sm">
          <a href="/policies" class="text-gray-300 hover:text-purple-400 transition-colors flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            Privacy Policy
          </a>
          <a href="/terms" class="text-gray-300 hover:text-pink-400 transition-colors flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            Terms of Service
          </a>
          <a href="/refund" class="text-gray-300 hover:text-purple-400 transition-colors flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
            </svg>
            Refund Policy
          </a>
        </div>
      </div>
    </div>
    
    <!-- Floating Sparkles -->
    <div class="absolute top-1/4 left-10 w-2 h-2 bg-purple-400 rounded-full animate-ping"></div>
    <div class="absolute top-1/3 right-16 w-3 h-3 bg-pink-400 rounded-full animate-ping" style="animation-delay: 1s;"></div>
    <div class="absolute bottom-1/4 left-1/3 w-2 h-2 bg-purple-300 rounded-full animate-ping" style="animation-delay: 2s;"></div>
    <div class="absolute top-2/3 right-1/3 w-2 h-2 bg-pink-300 rounded-full animate-ping" style="animation-delay: 0.5s;"></div>
  </div>
</template>

<script>
export default {
  name: 'NotFound',
  mounted() {
    // Track 404 page view for analytics
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: '404 - Page Not Found',
        page_location: window.location.href,
        page_path: window.location.pathname
      });
    }
  }
}
</script>
